import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Add from '@mui/icons-material/Add';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import Remove from '@mui/icons-material/Remove';
import { Box, Chip, Divider, Grid, IconButton, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import PageState from '../layouts/PageState';
import ProductChooseFormat, { FORMAT_MEAT } from '../components/product/ProductChooseFormat';
import ProductCustomizationSheet from '../components/product/ProductCustomizationSheet';
import ProductDetailHero from '../components/product/ProductDetailHero';
import ProductDetailInfo from '../components/product/ProductDetailInfo';
import {
  buildDefaultCustomizations,
  findOptionBySelection,
  findOptionBySku,
  isSkinlessSelected,
  isSmokeTurmericOption,
  optionKey,
} from '../components/product/productCustomizationHelpers';
import {
  buildProductAttributeLine,
  formatGrossNetWeights,
  formatLiveBirdPriceLabel,
  hasLiveBirdFormat,
  stockBadgeLabel,
} from '../components/product/productDetailHelpers';
import { useV2Common } from '../state/CommonProvider';
import Button from '../components/Button';
import { v2Colors } from '../design/theme';
import { storageGet } from '../platform/storage';
import {
  addToCartItem,
  computeLineUnitPrice,
  getCart,
  getCartLineForProduct,
  getProductById,
  resolveCustomizationsForCart,
  setCartLineQuantity,
} from '../services/commerce';

const LOCATION_CACHE_KEY = 'v2_location';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDesktop } = useV2Common();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [customizationOpen, setCustomizationOpen] = useState(false);
  const [selectedCustomizations, setSelectedCustomizations] = useState({});
  const [adding, setAdding] = useState(false);
  const [lineQty, setLineQty] = useState(0);
  const [cartLine, setCartLine] = useState(null);
  const [cartSummary, setCartSummary] = useState({ totalAmount: 0, totalCount: 0 });
  const [storeName, setStoreName] = useState(null);
  const [formatMode, setFormatMode] = useState(FORMAT_MEAT);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const cachedLocation = await storageGet(LOCATION_CACHE_KEY);
      const storeId = cachedLocation?.storeId ?? null;
      const res = await getProductById({ id, storeId });
      const p = res?.data ?? null;
      setProduct(p);

      const variants = (p?.variants ?? [])
        .filter((v) => v.visibleToCustomer !== false && v.isActive !== false);
      setSelectedVariantId(variants[0]?.sku ?? null);
      setSelectedImage(0);
      setSelectedCustomizations(buildDefaultCustomizations(p));
      setFormatMode(FORMAT_MEAT);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loc = await storageGet(LOCATION_CACHE_KEY);
      if (!cancelled) {
        setStoreName(loc?.storeName ?? loc?.store?.storeName ?? null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const images = useMemo(() => {
    if (!product) return [noImage];
    const list = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
    if (list.length) return list;
    if (product.image) return [product.image];
    return [noImage];
  }, [product]);

  const variants = useMemo(
    () => (product?.variants ?? []).filter((v) => v.visibleToCustomer !== false && v.isActive !== false),
    [product]
  );

  const selectedVariant = useMemo(
    () => variants.find((v) => v.sku === selectedVariantId) ?? variants[0] ?? null,
    [variants, selectedVariantId]
  );

  const customizations = useMemo(() => {
    const obj = product?.customizations ?? {};
    return Object.entries(obj);
  }, [product]);

  const resolvedCustomizations = useMemo(
    () => resolveCustomizationsForCart(product?.customizations, selectedCustomizations),
    [product?.customizations, selectedCustomizations]
  );

  const lineUnitPrice = useMemo(
    () =>
      selectedVariant ? computeLineUnitPrice(selectedVariant, resolvedCustomizations) : 0,
    [selectedVariant, resolvedCustomizations]
  );

  const variantForStock = useMemo(() => {
    if (lineQty > 0 && cartLine?.variant?.sku) {
      return variants.find((v) => v.sku === cartLine.variant.sku) ?? cartLine.variant;
    }
    return selectedVariant;
  }, [lineQty, cartLine, variants, selectedVariant]);

  /** List / catalog unit price only (variant or product); not cart line total with customizations. */
  const catalogUnitPrice = useMemo(
    () => Number(selectedVariant?.price) || Number(product?.price) || 0,
    [selectedVariant?.price, product?.price]
  );

  const showChooseFormat = useMemo(() => hasLiveBirdFormat(product), [product]);

  const refreshCartSummary = useCallback(async () => {
    const cart = await getCart();
    setCartSummary({
      totalAmount: Number(cart.totalAmount) || 0,
      totalCount: Number(cart.totalCount) || 0,
    });
  }, []);

  const applyBagLineFromStorage = useCallback(async () => {
    if (!product?.id) {
      setCartLine(null);
      setLineQty(0);
      await refreshCartSummary();
      return;
    }
    const line = await getCartLineForProduct(product.id);
    setCartLine(line);
    setLineQty(line ? Number(line.quantity) || 0 : 0);
    await refreshCartSummary();
  }, [product?.id, refreshCartSummary]);

  useEffect(() => {
    refreshCartSummary();
  }, [refreshCartSummary]);

  const handleCustomizationModalClose = useCallback(
    (_event, reason) => {
      setCustomizationOpen(false);
      const dismissed =
        reason === 'backdropClick' ||
        reason === 'escapeKeyDown' ||
        reason === undefined;
      if (dismissed && product) {
        setSelectedCustomizations(buildDefaultCustomizations(product));
      }
    },
    [product]
  );

  useEffect(() => {
    let cancelled = false;
    async function syncBag() {
      if (!product?.id) {
        if (!cancelled) {
          setCartLine(null);
          setLineQty(0);
        }
        return;
      }
      const line = await getCartLineForProduct(product.id);
      if (cancelled) return;
      setCartLine(line);
      setLineQty(line ? Number(line.quantity) || 0 : 0);
    }
    syncBag();
    return () => {
      cancelled = true;
    };
  }, [product?.id]);

  useEffect(() => {
    if (lineQty > 0 && cartLine?.variant?.sku) {
      setSelectedVariantId(cartLine.variant.sku);
    }
  }, [lineQty, cartLine?.variant?.sku]);

  const handleCustomizationSelectSingle = (groupKey, option) => {
    const selectedOptionId = optionKey(option);
    if (!selectedOptionId) return;

    setSelectedCustomizations((prev) => {
      // Guard: don't allow Smoke & Turmeric when Skinless is selected.
      if (
        groupKey === 'flavour' &&
        isSmokeTurmericOption(option) &&
        isSkinlessSelected(prev, product?.customizations)
      ) {
        return prev;
      }

      const next = { ...prev, [groupKey]: selectedOptionId };

      // If Type of Meat switches to Skinless, remove incompatible flavour.
      if (groupKey === 'type_of_meat' && isSkinlessSelected(next, product?.customizations)) {
        const flavourConfig = product?.customizations?.flavour;
        const selectedFlavour = findOptionBySelection(flavourConfig, next.flavour);
        if (isSmokeTurmericOption(selectedFlavour)) {
          const normalOption = findOptionBySku(flavourConfig, 'NML');
          if (normalOption) next.flavour = optionKey(normalOption);
          else delete next.flavour;
        }
      }

      return next;
    });
  };

  const handleCustomizationToggleCheckbox = (groupKey, option, checked) => {
    const id = optionKey(option);
    if (!id) return;
    setSelectedCustomizations((prev) => {
      const current = Array.isArray(prev[groupKey]) ? prev[groupKey] : [];
      const next = checked ? [...new Set([...current, id])] : current.filter((x) => x !== id);
      return { ...prev, [groupKey]: next };
    });
  };

  const handleAddToCartClick = () => {
    if (adding) return;
    if (customizations.length > 0) {
      setCustomizationOpen(true);
      return;
    }
    handleConfirmCustomizations();
  };

  const handleConfirmCustomizations = async () => {
    if (!product || !selectedVariant || adding) return;
    setAdding(true);
    try {
      await addToCartItem({
        productId: product.id,
        name: product.name,
        imageUrl: images[0] || noImage,
        variant: selectedVariant,
        customizations: resolvedCustomizations,
      });
      await applyBagLineFromStorage();
      setCustomizationOpen(false);
    } finally {
      setAdding(false);
    }
  };

  const handleDecrementQty = async () => {
    if (!product || lineQty <= 0) return;
    await setCartLineQuantity({ productId: product.id }, lineQty - 1);
    await applyBagLineFromStorage();
  };

  const handleIncrementQty = async () => {
    if (!product || adding || lineQty <= 0) return;
    await setCartLineQuantity({ productId: product.id }, lineQty + 1);
    await applyBagLineFromStorage();
  };

  const attributeLine = buildProductAttributeLine(product);
  const subCategory = product?.subCategory ? String(product.subCategory).trim() : '';

  const addToBagLabel = adding
    ? 'Adding to bag…'
    : customizations.length > 0
      ? 'Add to bag · customize →'
      : 'Add to bag →';

  const qtyStepperSx = (desktop) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: desktop ? 120 : '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    border: '1px solid',
    borderColor: v2Colors.borderMuted,
    borderRadius: desktop ? 2.5 : 10,
    bgcolor: desktop ? v2Colors.white : v2Colors.surface,
    px: desktop ? 0.5 : 1.5,
    py: 0,
    height: desktop ? 52 : undefined,
    minHeight: desktop ? 52 : 48,
    flexShrink: 0,
  });

  const qtyStepperBtnSx = {
    width: 40,
    height: 40,
    p: 0,
    flexShrink: 0,
    color: v2Colors.primary,
  };

  const qtyStepperValueSx = (desktop) => ({
    flex: 1,
    textAlign: 'center',
    fontWeight: 700,
    fontSize: desktop ? '1rem' : '1.25rem',
    lineHeight: 1,
    color: desktop ? v2Colors.heading : v2Colors.primary,
    userSelect: 'none',
  });

  const renderPurchaseContent = (desktop) => (
    <>
      {variants.length > 1 && (
        <Box sx={{ mt: 2.5 }}>
          {lineQty > 0 && cartLine?.variant ? (
            <>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Size in bag
              </Typography>
              <Chip
                variant="filled"
                color="primary"
                label={
                  cartLine.variant.weight
                    ? `${cartLine.variant.weight} · ₹${
                        Number(cartLine.price) || Number(cartLine.variant.price) || 0
                      }`
                    : `₹${Number(cartLine.price) || Number(cartLine.variant.price) || 0}`
                }
                sx={{ borderRadius: 1 }}
              />
            </>
          ) : (
            <>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                mb={1}
                sx={{ color: v2Colors.heading }}
              >
                Choose size
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {variants.map((v) => {
                  const selected = selectedVariant?.sku === v.sku;
                  return (
                    <Chip
                      key={v.sku}
                      clickable
                      onClick={() => setSelectedVariantId(v.sku)}
                      label={`${v.weight} · ₹${v.price}`}
                      disabled={v.isInStock === false}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        bgcolor: selected ? v2Colors.selectedFill : v2Colors.white,
                        color: selected ? v2Colors.primary : v2Colors.heading,
                        border: '1px solid',
                        borderColor: selected ? v2Colors.primary : v2Colors.borderMuted,
                      }}
                    />
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      )}

      {!(desktop && showChooseFormat) ? (
        <Box sx={{ mt: 2, minWidth: 0, maxWidth: desktop ? 'none' : { md: 400 } }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {lineQty > 0 && cartLine?.variant?.weight
              ? `Size in bag · ${cartLine.variant.weight}`
              : selectedVariant?.weight || (variants.length ? 'Select a size above' : 'Price')}
          </Typography>
          <Typography
            fontWeight={700}
            lineHeight={1.2}
            sx={{ mt: 0.25, fontSize: '1.5rem', color: v2Colors.heading }}
          >
            ₹{catalogUnitPrice}
          </Typography>
        </Box>
      ) : null}

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: desktop ? 'row' : 'column',
          alignItems: desktop ? 'center' : undefined,
          gap: desktop ? 1.25 : 1.5,
          maxWidth: desktop ? 'none' : { md: 400 },
        }}
      >
        {lineQty > 0 ? (
          <>
            <Box sx={qtyStepperSx(desktop)}>
              <IconButton
                size="small"
                aria-label="Decrease quantity"
                onClick={handleDecrementQty}
                disabled={lineQty <= 0}
                sx={qtyStepperBtnSx}
              >
                <Remove fontSize="small" />
              </IconButton>
              <Typography component="span" sx={qtyStepperValueSx(desktop)}>
                {lineQty}
              </Typography>
              <IconButton
                size="small"
                aria-label="Increase quantity"
                onClick={handleIncrementQty}
                disabled={
                  !variantForStock || variantForStock.isInStock === false || adding
                }
                sx={qtyStepperBtnSx}
              >
                <Add fontSize="small" />
              </IconButton>
            </Box>
            {desktop ? (
              <Button
                fullWidth
                size="large"
                onClick={() => navigate('/v2/cart')}
                sx={{
                  flex: 1,
                  minHeight: 52,
                  height: 52,
                  py: 0,
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2.5,
                }}
              >
                View bag ({lineQty})
              </Button>
            ) : null}
          </>
        ) : (
          <Button
              fullWidth
              size="large"
              disabled={
                (showChooseFormat && formatMode !== FORMAT_MEAT) ||
                !selectedVariant ||
                selectedVariant.isInStock === false ||
                adding
              }
              onClick={handleAddToCartClick}
              sx={{
                flex: desktop ? 1 : undefined,
                py: desktop ? 1.5 : 1.75,
                px: 3,
                fontSize: desktop ? '0.95rem' : '1.05rem',
                fontWeight: 700,
                letterSpacing: '0.01em',
                textTransform: 'none',
                borderRadius: desktop ? 2.5 : 9999,
                color: v2Colors.white,
                bgcolor: v2Colors.primary,
                boxShadow: '0 4px 14px rgba(165, 42, 60, 0.28)',
                '&:hover': {
                  bgcolor: v2Colors.primaryDark,
                  boxShadow: '0 6px 16px rgba(165, 42, 60, 0.32)',
                },
                '&.Mui-disabled': {
                  bgcolor: v2Colors.border,
                  color: v2Colors.white,
                  boxShadow: 'none',
                },
              }}
            >
              {addToBagLabel}
            </Button>
        )}
      </Box>

      {!desktop && product.description ? (
        <Box sx={{ mt: 2.5 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            mb={1}
            sx={{ color: v2Colors.heading }}
          >
            Description
          </Typography>
          <Typography variant="body2" sx={{ color: v2Colors.textMuted, lineHeight: 1.65 }}>
            {product.description}
          </Typography>
        </Box>
      ) : null}
    </>
  );

  return (
    <PageState loading={loading} error={error} onRetry={load} loadingMessage="Loading product...">
      {product ? (
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 0 },
            pt: 0.5,
            pb:
              !isDesktop && cartSummary.totalCount > 0
                ? (theme) =>
                    `calc(${theme.spacing(8)} + 56px + ${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`
                : 3,
            maxWidth: { md: isDesktop ? 960 : 720, lg: 1040 },
            mx: { md: 'auto' },
          }}
        >
          <IconButton
            aria-label="Go back"
            onClick={() => navigate(-1)}
            sx={{
              width: 40,
              height: 40,
              mt: 1.5,
              bgcolor: v2Colors.white,
              border: `1px solid ${v2Colors.primary}`,
              color: v2Colors.primary,
              '&:hover': { bgcolor: v2Colors.selectedFill },
            }}
          >
            <ArrowBackRounded fontSize="small" />
          </IconButton>

          {isDesktop ? (
            <Grid container spacing={3} alignItems="flex-start" sx={{ mt: 0.5 }}>
              <Grid item md={5} lg={4} sx={{ minWidth: 0 }}>
                <ProductDetailHero
                  images={images}
                  productName={product.name}
                  selectedImage={selectedImage}
                  onSelectImage={setSelectedImage}
                  stockLabel={stockBadgeLabel(product, storeName)}
                  inStock={Boolean(product.isStockAvailable)}
                />
              </Grid>
              <Grid item md={7} lg={8} sx={{ minWidth: 0 }}>
                <ProductDetailInfo
                  name={product.name}
                  aka={product.metadata?.aka}
                  attributeLine={attributeLine}
                  subCategory={subCategory || null}
                />
                {showChooseFormat ? (
                  <ProductChooseFormat
                    compact
                    meatPrice={catalogUnitPrice}
                    meatWeightLine={formatGrossNetWeights(product)}
                    liveBirdPriceLabel={formatLiveBirdPriceLabel(product)}
                    format={formatMode}
                    onSelectMeat={() => setFormatMode(FORMAT_MEAT)}
                  />
                ) : null}
                <Box sx={{ maxWidth: 480 }}>
                  {renderPurchaseContent(true)}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <>
              <ProductDetailHero
                images={images}
                productName={product.name}
                selectedImage={selectedImage}
                onSelectImage={setSelectedImage}
                stockLabel={stockBadgeLabel(product, storeName)}
                inStock={Boolean(product.isStockAvailable)}
              />
              <ProductDetailInfo
                name={product.name}
                aka={product.metadata?.aka}
                attributeLine={attributeLine}
                subCategory={subCategory || null}
              />
              {showChooseFormat ? (
                <ProductChooseFormat
                  meatPrice={catalogUnitPrice}
                  meatWeightLine={formatGrossNetWeights(product)}
                  liveBirdPriceLabel={formatLiveBirdPriceLabel(product)}
                  format={formatMode}
                  onSelectMeat={() => setFormatMode(FORMAT_MEAT)}
                />
              ) : null}
              <Box sx={{ maxWidth: { md: 480 } }}>
                {renderPurchaseContent(false)}
              </Box>
            </>
          )}

          {isDesktop && product.description ? (
            <Box sx={{ mt: 3, maxWidth: 640 }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                mb={1}
                sx={{ color: v2Colors.heading }}
              >
                Description
              </Typography>
              <Typography variant="body2" sx={{ color: v2Colors.textMuted, lineHeight: 1.65 }}>
                {product.description}
              </Typography>
            </Box>
          ) : null}

          <Divider sx={{ mt: 3, borderColor: v2Colors.border }} />

          {!isDesktop && cartSummary.totalCount > 0 ? (
            <Box
              sx={(theme) => ({
                position: 'fixed',
                zIndex: theme.zIndex.appBar,
                bgcolor: v2Colors.white,
                border: `1px solid ${v2Colors.border}`,
                borderRadius: 2,
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                boxShadow: 3,
                left: theme.spacing(2),
                right: theme.spacing(2),
                bottom: `calc(${theme.spacing(8)} + env(safe-area-inset-bottom, 0px))`,
              })}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={800} noWrap>
                  ₹{cartSummary.totalAmount}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {cartSummary.totalCount} {cartSummary.totalCount === 1 ? 'item' : 'items'}
                </Typography>
              </Box>
              <Button onClick={() => navigate('/v2/cart')} sx={{ flexShrink: 0 }}>
                View cart
              </Button>
            </Box>
          ) : null}

          <ProductCustomizationSheet
            open={customizationOpen}
            onClose={handleCustomizationModalClose}
            customizations={customizations}
            selectedCustomizations={selectedCustomizations}
            onSelectSingle={handleCustomizationSelectSingle}
            onToggleCheckbox={handleCustomizationToggleCheckbox}
            lineTotal={lineUnitPrice || selectedVariant?.price || product.price || 0}
            adding={adding}
            onConfirm={handleConfirmCustomizations}
          />
        </Box>
      ) : null}
    </PageState>
  );
}
